import { TextField } from "@mui/material";
import { endOfDay, isSameDay, isValid, parseISO, startOfDay } from "date-fns";
import { useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useAppSelector } from "../../../../../app/hooks";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import DateField from "../../../../../common/components/functional/dateField/DateField";
import DialogContentWrapper from "../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { AutocompleteSelectField } from "../../../../../common/components/functional/fields/selectField/AutocompleteSelectField";
import CommonSelectField from "../../../../../common/components/functional/fields/selectField/CommonSelectField";
import FormGridRow from "../../../../../common/components/functional/formGridRow/FormGridRow";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { validateDateInput } from "../../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../../common/helpers/isEmpty";
import {
  getFringeBenefitFormDefaultValues,
  mapFormValuesToFringeBenefit,
} from "./mappers";
import {
  FringeBenefit,
  FringeBenefitFormValues,
  FringeBenefitType,
} from "./types";

type Props = {
  close: () => void;
  onSave: (fringeBenefit: FringeBenefit) => void;
  fringeBenefits: FringeBenefit[];
  selectedFringeBenefit: FringeBenefit | null;
  fringeBenefitTypes: FringeBenefitType[];
};

export const FringeBenefitForm = ({
  close,
  onSave,
  fringeBenefits,
  selectedFringeBenefit,
  fringeBenefitTypes,
}: Props) => {
  const { t } = useTranslation();
  const isEdit = !!selectedFringeBenefit?.id;

  const defaultValues = useMemo(
    () => getFringeBenefitFormDefaultValues(selectedFringeBenefit),
    [selectedFringeBenefit],
  );

  const methods = useForm<FringeBenefitFormValues>({
    defaultValues,
    mode: "onChange",
  });
  const {
    reset,
    control,
    watch,
    trigger,
    getValues,
    handleSubmit,
    formState: { isDirty, errors, isValid: isValidForm },
  } = methods;

  const expenseTypeCode = watch("expenseTypeCode");
  const employment = useAppSelector((state) => state.employment.additionalInfo);
  const employmentStart = employment?.details?.agreedStart;
  const employmentEnd = employment?.details?.agreedEnd;

  const selectedFringeBenefitType = fringeBenefitTypes.find(
    (type) => type.code === expenseTypeCode,
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const validateWithinEmployment = (date: Date | null) => {
    if (!date || !isValid(date) || !validateDateInput(date)) {
      return t("common:invalidDate");
    }
    if (!employmentStart) return true;

    const employmentStartDate = startOfDay(new Date(employmentStart));
    const employmentEndDate = employmentEnd
      ? endOfDay(new Date(employmentEnd))
      : null;
    const value = startOfDay(date);

    if (employmentEndDate) {
      if (value < employmentStartDate || value > employmentEndDate) {
        return t("talentProfile:dateOutsideEmployment");
      }
    } else if (value < employmentStartDate) {
      return t("talentProfile:dateBeforeEmploymentStart");
    }

    return true;
  };

  const validateNoDuplicateStartDateInTypeGroup = (
    date: Date | null,
    typeCode: string,
  ) => {
    if (!date) return true;
    const startDates = fringeBenefits
      // Exclude the current fringe benefit from the check
      .filter((benefit) => benefit.id !== selectedFringeBenefit?.id)
      .filter((benefit) => benefit.expenseTypeCode === typeCode)
      .map((benefit) => parseISO(benefit.startDate));
    return startDates.some((startDate) => isSameDay(startDate, date))
      ? t("talentProfile:duplicateStartDate")
      : true;
  };

  const validateEndDate = (
    endDate: Date | null,
    startDate: Date | null,
    typeCode: string,
  ) => {
    if (!endDate) return true;
    const withinEmployment = validateWithinEmployment(endDate);
    if (withinEmployment !== true) return withinEmployment;
    if (!startDate || !isValid(startDate)) return true;
    if (startOfDay(endDate) < startOfDay(startDate)) {
      return t("talentProfile:endDateBeforeStartDate");
    }
    const overlapsNext = fringeBenefits
      .filter((benefit) => benefit.id !== selectedFringeBenefit?.id)
      .filter((benefit) => benefit.expenseTypeCode === typeCode)
      .map((benefit) => parseISO(benefit.startDate))
      .some(
        (nextStart) =>
          startOfDay(nextStart) > startOfDay(startDate) &&
          startOfDay(nextStart) <= startOfDay(endDate),
      );
    return overlapsNext ? t("talentProfile:endDateOverlapsNextBenefit") : true;
  };

  // End date validity depends on the start date and the expense type
  const startDate = watch("startDate");
  useEffect(() => {
    if (getValues("endDate")) trigger("endDate");
  }, [startDate, expenseTypeCode, getValues, trigger]);

  const onSubmit = handleSubmit((values) => {
    onSave(
      mapFormValuesToFringeBenefit(values, selectedFringeBenefitType?.fields),
    );
  });

  return (
    <DialogContentWrapper
      margin
      title={
        isEdit
          ? t("talentProfile:editFringeBenefit")
          : t("talentProfile:addFringeBenefit")
      }
      onCancel={close}
      onSave={onSubmit}
      saveDisabled={(isEdit && !isDirty) || !isEmpty(errors) || !isValidForm}
    >
      <FormProvider {...methods}>
        <FormGridRow label={t("talentProfile:expenseType")}>
          <Controller
            name="expenseTypeCode"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <CommonSelectField
                value={value}
                onChange={(event) => {
                  onChange(event.target.value);
                  if (getValues("startDate")) trigger("startDate");
                }}
                options={fringeBenefitTypes.map((type) => ({
                  value: type.code,
                  label: type.name,
                }))}
                error={!!errors.expenseTypeCode}
              />
            )}
          />
        </FormGridRow>
        {selectedFringeBenefitType?.fields?.map((typeField) => {
          const hasError = !!errors.details?.[typeField.name];
          if (typeField.type === "ENUM") {
            return (
              <FormGridRow label={typeField.label} key={typeField.name}>
                <Controller
                  name={`details.${typeField.name}`}
                  control={control}
                  rules={{ required: typeField.required }}
                  render={({ field }) => {
                    return (
                      <CommonSelectField
                        value={field.value ?? ""}
                        onChange={(event) => field.onChange(event.target.value)}
                        options={typeField.values.map((value) => ({
                          value: value,
                          label: value,
                        }))}
                        error={hasError}
                      />
                    );
                  }}
                />
              </FormGridRow>
            );
          } else {
            return (
              <FormGridRow label={typeField.label} key={typeField.name}>
                <Controller
                  name={`details.${typeField.name}`}
                  control={control}
                  rules={{ required: typeField.required }}
                  render={({ field }) => (
                    <CommonTextField
                      fullWidth
                      {...field}
                      value={field.value ?? ""}
                      error={hasError}
                    />
                  )}
                />
              </FormGridRow>
            );
          }
        })}
        <FormGridRow label={t("common:description")}>
          <Controller
            name="description"
            control={control}
            rules={{ maxLength: 128 }}
            render={({ field }) => (
              <CommonTextField
                fullWidth
                {...field}
                inputProps={{ maxLength: 128 }}
              />
            )}
          />
        </FormGridRow>
        <FormGridRow
          label={t("common:additionalInformation")}
          tooltip={
            <InfoTooltip
              title={t("talentProfile:onlyShownToExperts")}
              style={{ marginLeft: "4px" }}
            />
          }
        >
          <Controller
            name="additionalInformation"
            control={control}
            render={({ field }) => <CommonTextField fullWidth {...field} />}
          />
        </FormGridRow>
        <FormGridRow label={t("talentProfile:startDate")}>
          <DateField
            name="startDate"
            required={{ value: true, message: "" }}
            error={!!errors.startDate}
            validate={(date, fieldValues) => {
              const withinEmployment = validateWithinEmployment(date);
              if (withinEmployment !== true) return withinEmployment;
              return validateNoDuplicateStartDateInTypeGroup(
                date,
                fieldValues?.expenseTypeCode,
              );
            }}
            minDate={employmentStart ? new Date(employmentStart) : undefined}
            maxDate={employmentEnd ? new Date(employmentEnd) : undefined}
          />
        </FormGridRow>
        <FormGridRow label={t("talentProfile:endDate")}>
          <DateField
            name="endDate"
            required={{ value: false, message: "" }}
            error={!!errors.endDate}
            validate={(date, fieldValues) =>
              validateEndDate(
                date,
                fieldValues?.startDate,
                fieldValues?.expenseTypeCode,
              )
            }
            minDate={employmentStart ? new Date(employmentStart) : undefined}
            maxDate={employmentEnd ? new Date(employmentEnd) : undefined}
          />
        </FormGridRow>
        <FormGridRow label={t("talentProfile:amount")}>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: true,
              validate: (value) =>
                !!value && value.slice(-1) !== "." && parseFloat(value) > 0,
            }}
            render={({ field }) => (
              <NumericFormat
                value={field.value}
                decimalSeparator=","
                valueIsNumericString
                customInput={TextField}
                decimalScale={2}
                size="small"
                fullWidth
                allowNegative={false}
                onValueChange={(values) => {
                  field.onChange(values.value);
                }}
              />
            )}
          />
        </FormGridRow>
        <FormGridRow label={t("talentProfile:includedInTotalSalaryLong")}>
          <AutocompleteSelectField name="includedInTotalSalary" />
        </FormGridRow>
      </FormProvider>
    </DialogContentWrapper>
  );
};
